/**
 * Display-only Arabic translation map for canonical English values stored
 * in the database (Sedan, Automatic, Petrol, Riyadh…).  Used by VehicleCard
 * and VehicleDetailPage so that an Arabic visitor never sees raw English
 * value text. Falls back to the raw value if no translation exists.
 *
 * Keep this in sync with the dropdown options in SellPage.jsx.
 */
export const AR_VALUE_MAP = {
  // body types
  Sedan: "سيدان", SUV: "دفع رباعي", Crossover: "كروس أوفر", "Off-road": "دفع رباعي (طرق وعرة)",
  Hatchback: "هاتشباك", Coupe: "كوبيه", Convertible: "مكشوفة", Cabriolet: "كابريوليه",
  Roadster: "رودستر", Muscle: "أمريكي عضلي", Sport: "رياضية", Wagon: "ستيشن",
  Pickup: "بيك أب", Van: "فان", Minivan: "مينيفان", Micro: "اقتصادية صغيرة",
  Limousine: "ليموزين", Truck: "شاحنة", Bus: "حافلة", Other: "أخرى",

  // transmissions
  Automatic: "أوتوماتيك", Manual: "عادي", DCT: "DCT ثنائي القابض", CVT: "CVT متغير",

  // fuels
  Petrol: "بنزين", Diesel: "ديزل", Hybrid: "هايبرد", Electric: "كهربائي", LPG: "غاز مسال",

  // Saudi cities
  Riyadh: "الرياض", Jeddah: "جدة", Makkah: "مكة", Madinah: "المدينة", Dammam: "الدمام",
  Khobar: "الخبر", Dhahran: "الظهران", Taif: "الطائف", Tabuk: "تبوك", Abha: "أبها",
  Hail: "حائل", Buraidah: "بريدة", Hofuf: "الهفوف", Najran: "نجران", Jizan: "جازان",
  Yanbu: "ينبع", Jubail: "الجبيل", Sakaka: "سكاكا", Arar: "عرعر", Qatif: "القطيف",

  // seller types & vehicle listing types
  individual: "فردي", dealer: "تاجر", agency: "وكالة", showroom: "معرض",
  rental: "تأجير", financing: "تمويل", insurance: "تأمين", inspection: "فحص",
  logistics: "شحن ونقل", workshop: "ورشة", parts: "قطع غيار", fleet: "أسطول",
  corporate: "شركة", exporter: "مُصدِّر",
  used: "مستعملة", new: "جديدة", salvage: "سكراب",
  // iter276 — display label for the new "customs card" listing state.
  // Only affects listings that opt into this type; all others remain
  // labelled by their existing key.
  // iter280 — dropped the 🆕 emoji per product owner (label-only).
  new_customs_card: "جديدة – بطاقة جمركية",
  motorcycle: "دراجة", heavy: "ثقيلة", export: "للتصدير",
};

// iter280 — English display map. Historically the app relied on the
// canonical (English) storage value being human-readable in the
// English UI. That holds for every legacy value, but a new key like
// `new_customs_card` still needs a friendlier label — this map is
// consulted ONLY when a specific EN override is defined.
export const EN_VALUE_MAP = {
  new_customs_card: "New – Customs Card",
};

// iter188 — merge in every Saudi city from the unified locations
// registry so newly-added cities/governorates get an Arabic label
// automatically without touching this file.
import { SAUDI_CITIES } from "../data/locations";
for (const [id, , ar] of SAUDI_CITIES) {
  if (ar && !AR_VALUE_MAP[id]) AR_VALUE_MAP[id] = ar;
}

/** Translates a stored canonical value to the user's active language. */
export function localizeValue(value, lang) {
  if (lang !== "ar") {
    // iter280 — fall through the EN override map so keys like
    // `new_customs_card` render as "New – Customs Card" instead of
    // exposing the raw storage token in the English UI.
    return EN_VALUE_MAP[value] || value;
  }
  return AR_VALUE_MAP[value] || value;
}
